import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { GhostButton } from '../ui/GhostButton';
import { Trash, Edit, Check, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const ResourceTypeManagementModal = ({ isOpen, onClose, defaultCategory = 'Software' }) => {
    const { resourceTypes, addResourceType, updateResourceType, deleteResourceType, syncResourceVisibility, fetchChargeDocuments, activeCharge } = usePIM();
    const [newType, setNewType] = useState({ name: '', icon: 'File', description: '', category: defaultCategory, color: '#000000', default_visibility: 'sensitive' });
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});

    const filteredTypes = resourceTypes.filter(t => t.category === defaultCategory);
    const { items: sortedTypes, requestSort, sortConfig } = useSortableData(filteredTypes);

    const getIcon = (iconName) => {
        const Icon = Icons[iconName] || Icons.File;
        return <Icon size={16} />;
    };

    const handleAdd = async () => {
        if (!newType.name) return;
        await addResourceType({ ...newType, category: defaultCategory });
        setNewType({ name: '', icon: 'File', description: '', category: defaultCategory, color: '#000000', default_visibility: 'sensitive' });
    };

    const handleSync = async () => {
        if (window.confirm(`Möchten Sie wirklich ALLE existierenden Dokumente und Software-Einträge auf die aktuellen Standard-Sichtbarkeitseinstellungen (Visibility) ihrer jeweiligen Typen zurücksetzen?`)) {
            const success = await syncResourceVisibility();
            if (success && activeCharge?.id) {
                // Optional: Refresh documents in background if active charge is known
                fetchChargeDocuments(activeCharge.id);
            }
        }
    };

    const startEdit = (type) => {
        setEditingId(type.id);
        setEditData(type);
    };

    const saveEdit = async () => {
        await updateResourceType(editingId, editData);
        setEditingId(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Manage ${defaultCategory} Types`} maxWidth="max-w-5xl">
            <div className="space-y-6">
                {/* Add New */}
                <div className="bg-stone-900/50 p-4 rounded-lg border border-stone-800 flex gap-4 items-end">
                    <div className="w-20">
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Sort</label>
                        <Input
                            type="number"
                            value={newType.sort_order || ''}
                            onChange={(e) => setNewType({ ...newType, sort_order: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Name</label>
                        <Input
                            value={newType.name || ''}
                            onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                            placeholder="New Type Name"
                        />
                    </div>
                    <div className="w-32">
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Icon</label>
                        <Input
                            value={newType.icon || ''}
                            onChange={(e) => setNewType({ ...newType, icon: e.target.value })}
                            placeholder="Lucide Icon"
                        />
                    </div>
                    <div className="w-24">
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Color</label>
                        <div className="flex items-center gap-2 h-10">
                            <input
                                type="color"
                                value={newType.color?.startsWith('#') ? newType.color : '#000000'}
                                onChange={(e) => setNewType({ ...newType, color: e.target.value })}
                                className="h-full w-full rounded bg-transparent cursor-pointer border border-stone-700"
                            />
                        </div>
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Description</label>
                        <Input
                            value={newType.description || ''}
                            onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                            placeholder="Description"
                        />
                    </div>
                    <div className="w-32">
                        <label className="block text-xs font-semibold text-stone-400 uppercase mb-1">Visibility</label>
                        <div className="h-10 relative">
                            <select
                                value={newType.default_visibility || 'sensitive'}
                                onChange={(e) => setNewType({ ...newType, default_visibility: e.target.value })}
                                className="w-full h-full bg-stone-900 border border-stone-700 rounded px-2 text-sm text-stone-200 outline-none focus:border-purple-500 appearance-none cursor-pointer"
                            >
                                <option value="public">Public</option>
                                <option value="sensitive">Sensitive</option>
                                <option value="blocked">Blocked</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <PrimaryButton onClick={handleAdd}>Add</PrimaryButton>
                    </div>
                </div>

                <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-900/30 flex justify-between items-center">
                    <div className="text-sm text-amber-200/70 max-w-2xl">
                        <span className="font-bold text-amber-400">Visibility Sync:</span> Passt rückwirkend die Sichtbarkeit ALLER bereits hochgeladenen Dokumente an die hier definierten Standard-Einstellungen an.
                    </div>
                    <button 
                        onClick={handleSync}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-2"
                        title="Alle Dokumente mit diesen Einstellungen synchronisieren"
                    >
                        <Icons.RefreshCw size={16} />
                        Sync All Documents
                    </button>
                </div>

                {/* List */}
                <div className="border border-stone-800 rounded-lg overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-stone-950">
                            <tr>
                                <SortableHeader label="Sort" sortKey="sort_order" currentSort={sortConfig} onSort={requestSort} className="w-16 px-4 py-3" />
                                <SortableHeader label="Icon" sortKey="icon" currentSort={sortConfig} onSort={requestSort} className="w-10 px-4 py-3" />
                                <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} className="px-4 py-3" />
                                <SortableHeader label="Color" sortKey="color" currentSort={sortConfig} onSort={requestSort} className="w-24 px-4 py-3" />
                                <SortableHeader label="Visibility" sortKey="default_visibility" currentSort={sortConfig} onSort={requestSort} className="w-28 px-4 py-3" />
                                <SortableHeader label="Description" sortKey="description" currentSort={sortConfig} onSort={requestSort} className="px-4 py-3" />
                                <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase border-b border-stone-800 text-right w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTypes.map(type => (
                                <tr key={type.id} className="border-b border-stone-800 last:border-0 hover:bg-stone-900/30">
                                    {editingId === type.id ? (
                                        <>
                                            <td className="px-4 py-2">
                                                <Input
                                                    type="number"
                                                    value={editData.sort_order || ''}
                                                    onChange={(e) => setEditData({ ...editData, sort_order: parseInt(e.target.value) || 0 })}
                                                    className="w-16"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <Input
                                                    value={editData.icon || ''}
                                                    onChange={(e) => setEditData({ ...editData, icon: e.target.value })}
                                                    className="w-24"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <Input
                                                    value={editData.name || ''}
                                                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                                    className="w-full"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <input
                                                    type="color"
                                                    value={editData.color?.startsWith('#') ? editData.color : '#3b82f6'}
                                                    onChange={(e) => setEditData({ ...editData, color: e.target.value })}
                                                    className="h-8 w-full rounded bg-transparent cursor-pointer border border-stone-700"
                                                />
                                            </td>
                                            <td className="px-4 py-2">
                                                <select
                                                    value={editData.default_visibility || 'sensitive'}
                                                    onChange={(e) => setEditData({ ...editData, default_visibility: e.target.value })}
                                                    className="w-full h-8 bg-stone-900 border border-stone-700 rounded px-2 text-sm text-stone-200 outline-none focus:border-purple-500"
                                                >
                                                    <option value="public">Public</option>
                                                    <option value="sensitive">Sensitive</option>
                                                    <option value="blocked">Blocked</option>
                                                </select>
                                            </td>
                                            <td className="px-4 py-2">
                                                <Input
                                                    value={editData.description || ''}
                                                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                                    className="w-full"
                                                />
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={saveEdit} className="text-green-400 hover:text-green-300"><Check size={16} /></button>
                                                    <button onClick={cancelEdit} className="text-red-400 hover:text-red-300"><X size={16} /></button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-4 py-3 text-stone-400 text-sm">{type.sort_order}</td>
                                            <td className="px-4 py-3 text-stone-400">{getIcon(type.icon)}</td>
                                            <td className="px-4 py-3 text-stone-200 font-medium">{type.name}</td>
                                            <td className="px-4 py-3">
                                                <div
                                                    className="w-6 h-6 rounded border border-stone-700"
                                                    style={{ backgroundColor: type.color || '#3b82f6' }}
                                                    title={type.color}
                                                ></div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${type.default_visibility === 'public' ? 'bg-green-900/20 text-green-400 border-green-900/50' :
                                                    type.default_visibility === 'blocked' ? 'bg-red-900/20 text-red-400 border-red-900/50' :
                                                        'bg-yellow-900/20 text-yellow-400 border-yellow-900/50'
                                                    }`}>
                                                    {type.default_visibility || 'sensitive'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-stone-400 text-sm">{type.description}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => startEdit(type)} className="text-stone-400 hover:text-blue-400"><Edit size={14} /></button>
                                                    <button onClick={() => deleteResourceType(type.id)} className="text-stone-400 hover:text-red-400"><Trash size={14} /></button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex justify-end">
                    <GhostButton onClick={onClose}>Close</GhostButton>
                </div>
            </div>
        </Modal>
    );
};
